/**
 * Automated Cross-Browser Animation Testing
 * Provides automated testing capabilities for animation performance and compatibility
 */

import { crossBrowserTester, type AnimationTestResult } from './crossBrowserTesting';
import { getBrowserInfo, getAnimationCapabilities } from './browserCompatibility';
import { performanceOptimizer } from './performanceOptimizer';

export interface TestSuite {
  name: string;
  tests: AutomatedTest[];
  setup?: () => Promise<void>;
  teardown?: () => Promise<void>;
}

export interface AutomatedTest {
  name: string;
  description: string;
  selector: string;
  animationType: 'css' | 'framer-motion' | 'react-spring' | 'web-animations';
  expectedDuration: number;
  expectedFPS: number;
  complexity: 'low' | 'medium' | 'high';
  execute: () => Promise<void>;
  validate: (element: HTMLElement) => Promise<boolean>;
}

export interface TestReport {
  browserInfo: {
    name: string;
    version: number;
    engine: string;
  };
  timestamp: string;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  averagePerformance: {
    fps: number;
    memoryUsage: number;
    droppedFrames: number;
  };
  testResults: AnimationTestResult[];
  recommendations: string[];
}

class AutomatedAnimationTester {
  private testSuites: Map<string, TestSuite> = new Map();
  private currentResults: AnimationTestResult[] = [];
  private performanceData: Array<{
    timestamp: number;
    fps: number;
    memoryUsage: number;
  }> = [];

  constructor() {
    this.initializeDefaultTestSuites();
    this.startPerformanceMonitoring();
  }

  private initializeDefaultTestSuites(): void {
    // Basic Animation Test Suite
    this.registerTestSuite({
      name: 'basic-animations',
      tests: [
        {
          name: 'fade-in-test',
          description: 'Tests basic fade-in animation performance',
          selector: '.fade-test-element',
          animationType: 'css',
          expectedDuration: 300,
          expectedFPS: 60,
          complexity: 'low',
          execute: async () => {
            const element = this.createTestElement('fade-test-element');
            element.style.opacity = '0';
            element.style.transition = 'opacity 300ms ease-out';
            document.body.appendChild(element);
            
            // Trigger animation
            requestAnimationFrame(() => {
              element.style.opacity = '1';
            });
          },
          validate: async (element) => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const opacity = parseFloat(getComputedStyle(element).opacity);
                resolve(opacity === 1);
              }, 350);
            });
          }
        },
        {
          name: 'transform-test',
          description: 'Tests transform animation performance',
          selector: '.transform-test-element',
          animationType: 'css',
          expectedDuration: 500,
          expectedFPS: 60,
          complexity: 'medium',
          execute: async () => {
            const element = this.createTestElement('transform-test-element');
            element.style.transform = 'translateX(-100px) scale(0.5)';
            element.style.transition = 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)';
            document.body.appendChild(element);
            
            requestAnimationFrame(() => {
              element.style.transform = 'translateX(0) scale(1)';
            });
          },
          validate: async (element) => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const transform = getComputedStyle(element).transform;
                resolve(transform === 'matrix(1, 0, 0, 1, 0, 0)' || transform === 'none');
              }, 550);
            });
          }
        },
        {
          name: '3d-transform-test',
          description: 'Tests 3D transform animation performance',
          selector: '.transform-3d-test-element',
          animationType: 'css',
          expectedDuration: 1000,
          expectedFPS: 60,
          complexity: 'high',
          execute: async () => {
            const element = this.createTestElement('transform-3d-test-element');
            element.style.transform = 'perspective(1000px) rotateY(-90deg)';
            element.style.transition = 'transform 1000ms ease-in-out';
            element.style.transformStyle = 'preserve-3d';
            document.body.appendChild(element);
            
            requestAnimationFrame(() => {
              element.style.transform = 'perspective(1000px) rotateY(0deg)';
            });
          },
          validate: async (element) => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const transform = getComputedStyle(element).transform;
                // Check if transform completed (should be close to identity matrix)
                resolve(transform.includes('matrix3d') || transform === 'none');
              }, 1050);
            });
          }
        }
      ]
    });

    // Performance Stress Test Suite
    this.registerTestSuite({
      name: 'performance-stress',
      tests: [
        {
          name: 'concurrent-animations-test',
          description: 'Tests performance with multiple concurrent animations',
          selector: '.concurrent-test-element',
          animationType: 'css',
          expectedDuration: 2000,
          expectedFPS: 30, // Lower expectation for stress test
          complexity: 'high',
          execute: async () => {
            const maxAnimations = getAnimationCapabilities().maxConcurrentAnimations;
            
            for (let i = 0; i < maxAnimations; i++) {
              const element = this.createTestElement(`concurrent-test-element-${i}`);
              element.style.transform = `translateX(${-100 - i * 10}px)`;
              element.style.transition = 'transform 2000ms ease-in-out';
              element.style.animationDelay = `${i * 100}ms`;
              document.body.appendChild(element);
              
              setTimeout(() => {
                element.style.transform = `translateX(${100 + i * 10}px)`;
              }, i * 50);
            }
          },
          validate: async () => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const metrics = performanceOptimizer.getMetrics();
                resolve(metrics.averageFPS > 20); // Minimum acceptable FPS
              }, 2500);
            });
          }
        }
      ]
    });

    // Browser-Specific Test Suite
    this.registerTestSuite({
      name: 'browser-specific',
      tests: [
        {
          name: 'backdrop-filter-test',
          description: 'Tests backdrop-filter support and performance',
          selector: '.backdrop-filter-test-element',
          animationType: 'css',
          expectedDuration: 300,
          expectedFPS: 60,
          complexity: 'high',
          execute: async () => {
            const container = this.createTestElement('backdrop-container');
            container.style.background = 'linear-gradient(45deg, red, blue)';
            container.style.width = '200px';
            container.style.height = '200px';
            
            const element = this.createTestElement('backdrop-filter-test-element');
            element.style.backdropFilter = 'blur(10px)';
            element.style.background = 'rgba(255, 255, 255, 0.2)';
            element.style.width = '100px';
            element.style.height = '100px';
            element.style.position = 'absolute';
            element.style.top = '50px';
            element.style.left = '50px';
            element.style.opacity = '0';
            element.style.transition = 'opacity 300ms ease-out';
            
            container.appendChild(element);
            document.body.appendChild(container);
            
            requestAnimationFrame(() => {
              element.style.opacity = '1';
            });
          },
          validate: async (element) => {
            return new Promise((resolve) => {
              setTimeout(() => {
                const style = getComputedStyle(element);
                const hasBackdropFilter = style.backdropFilter !== 'none';
                const isVisible = parseFloat(style.opacity) === 1;
                resolve(hasBackdropFilter && isVisible);
              }, 350);
            });
          }
        }
      ]
    });
  }

  private createTestElement(className: string): HTMLElement {
    const element = document.createElement('div');
    element.className = className;
    element.style.width = '50px';
    element.style.height = '50px';
    element.style.background = '#3498db';
    element.style.position = 'absolute';
    element.style.top = '10px';
    element.style.left = '10px';
    element.style.zIndex = '9999';
    return element;
  }

  private startPerformanceMonitoring(): void {
    setInterval(() => {
      const metrics = performanceOptimizer.getMetrics();
      this.performanceData.push({
        timestamp: Date.now(),
        fps: metrics.averageFPS,
        memoryUsage: metrics.memoryUsage
      });

      // Keep only last 100 data points
      if (this.performanceData.length > 100) {
        this.performanceData.shift();
      }
    }, 1000);
  }

  public registerTestSuite(testSuite: TestSuite): void {
    this.testSuites.set(testSuite.name, testSuite);
  }

  public async runTestSuite(suiteName: string): Promise<AnimationTestResult[]> {
    const testSuite = this.testSuites.get(suiteName);
    if (!testSuite) {
      throw new Error(`Test suite "${suiteName}" not found`);
    }

    console.log(`Running test suite: ${testSuite.name}`);
    
    if (testSuite.setup) {
      await testSuite.setup();
    }

    const results: AnimationTestResult[] = [];

    for (const test of testSuite.tests) {
      try {
        console.log(`Running test: ${test.name}`);
        
        // Skip high complexity tests if not supported
        if (test.complexity === 'high' && !performanceOptimizer.getSettings().enableComplexAnimations) {
          console.log(`Skipping high complexity test: ${test.name}`);
          continue;
        }

        const result = await this.runSingleTest(test);
        results.push(result);
        
        // Clean up test elements
        this.cleanupTestElements(test.selector);
        
        // Small delay between tests
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        console.error(`Test ${test.name} failed:`, error);
        
        const errorResult: AnimationTestResult = {
          testName: test.name,
          browser: `${getBrowserInfo().name} ${getBrowserInfo().version}`,
          passed: false,
          duration: 0,
          frameRate: 0,
          errors: [`Test execution failed: ${error}`],
          warnings: [],
          performance: {
            averageFPS: 0,
            droppedFrames: 0,
            memoryUsage: 0
          }
        };
        
        results.push(errorResult);
      }
    }

    if (testSuite.teardown) {
      await testSuite.teardown();
    }

    this.currentResults.push(...results);
    return results;
  }

  private async runSingleTest(test: AutomatedTest): Promise<AnimationTestResult> {
    const browserInfo = getBrowserInfo();
    const startTime = performance.now();
    const startMetrics = performanceOptimizer.getMetrics();

    const result: AnimationTestResult = {
      testName: test.name,
      browser: `${browserInfo.name} ${browserInfo.version}`,
      passed: false,
      duration: 0,
      frameRate: 0,
      errors: [],
      warnings: [],
      performance: {
        averageFPS: 0,
        droppedFrames: 0,
        memoryUsage: 0
      }
    };

    try {
      // Execute the test
      await test.execute();
      
      // Wait for animation to complete
      await new Promise(resolve => setTimeout(resolve, test.expectedDuration + 100));
      
      // Validate the test
      const element = document.querySelector(test.selector) as HTMLElement;
      if (element) {
        const isValid = await test.validate(element);
        result.passed = isValid;
        
        if (!isValid) {
          result.errors.push('Animation validation failed');
        }
      } else {
        result.errors.push('Test element not found');
      }

      // Calculate performance metrics
      const endMetrics = performanceOptimizer.getMetrics();
      result.performance.averageFPS = endMetrics.averageFPS;
      result.performance.droppedFrames = endMetrics.frameDrops - startMetrics.frameDrops;
      result.performance.memoryUsage = endMetrics.memoryUsage;

      // Performance validation
      if (result.performance.averageFPS < test.expectedFPS * 0.8) {
        result.warnings.push(`Low FPS: ${result.performance.averageFPS.toFixed(1)} (expected: ${test.expectedFPS})`);
      }

      if (result.performance.droppedFrames > 10) {
        result.warnings.push(`High frame drops: ${result.performance.droppedFrames}`);
      }

    } catch (error) {
      result.errors.push(`Test execution error: ${error}`);
    }

    result.duration = performance.now() - startTime;
    return result;
  }

  private cleanupTestElements(selector: string): void {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
  }

  public async runAllTestSuites(): Promise<TestReport> {
    const browserInfo = getBrowserInfo();
    this.currentResults = [];

    for (const [suiteName] of this.testSuites) {
      await this.runTestSuite(suiteName);
    }

    return this.generateReport();
  }

  public generateReport(): TestReport {
    const browserInfo = getBrowserInfo();
    const passedTests = this.currentResults.filter(r => r.passed).length;
    const failedTests = this.currentResults.length - passedTests;

    // Calculate average performance
    const avgFPS = this.currentResults.reduce((sum, r) => sum + r.performance.averageFPS, 0) / this.currentResults.length;
    const avgMemory = this.currentResults.reduce((sum, r) => sum + r.performance.memoryUsage, 0) / this.currentResults.length;
    const totalDroppedFrames = this.currentResults.reduce((sum, r) => sum + r.performance.droppedFrames, 0);

    // Generate recommendations
    const recommendations = this.generateRecommendations();

    return {
      browserInfo: {
        name: browserInfo.name,
        version: browserInfo.version,
        engine: browserInfo.engine
      },
      timestamp: new Date().toISOString(),
      totalTests: this.currentResults.length,
      passedTests,
      failedTests,
      averagePerformance: {
        fps: avgFPS,
        memoryUsage: avgMemory,
        droppedFrames: totalDroppedFrames
      },
      testResults: this.currentResults,
      recommendations
    };
  }

  private generateRecommendations(): string[] {
    const recommendations: string[] = [];
    const browserInfo = getBrowserInfo();
    const capabilities = getAnimationCapabilities();
    const avgFPS = this.currentResults.reduce((sum, r) => sum + r.performance.averageFPS, 0) / this.currentResults.length;

    if (avgFPS < 30) {
      recommendations.push('Consider reducing animation complexity or duration for better performance');
    }

    if (browserInfo.name === 'safari' && browserInfo.version < 14) {
      recommendations.push('Update Safari for better backdrop-filter support');
    }

    if (browserInfo.name === 'firefox' && browserInfo.version < 103) {
      recommendations.push('Update Firefox for better backdrop-filter support');
    }

    if (!capabilities.supportsComplexTransforms) {
      recommendations.push('Use simpler animations for better compatibility');
    }

    if (capabilities.maxConcurrentAnimations < 5) {
      recommendations.push('Limit concurrent animations to improve performance');
    }

    const failedTests = this.currentResults.filter(r => !r.passed);
    if (failedTests.length > 0) {
      recommendations.push(`${failedTests.length} tests failed - check browser compatibility`);
    }

    return recommendations;
  }

  public exportReport(format: 'json' | 'html' | 'csv' = 'json'): string {
    const report = this.generateReport();

    switch (format) {
      case 'json':
        return JSON.stringify(report, null, 2);
      
      case 'html':
        return this.generateHTMLReport(report);
      
      case 'csv':
        return this.generateCSVReport(report);
      
      default:
        return JSON.stringify(report, null, 2);
    }
  }

  private generateHTMLReport(report: TestReport): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <title>Animation Test Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { background: #f5f5f5; padding: 20px; border-radius: 8px; }
        .summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin: 20px 0; }
        .metric { background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .test-result { margin: 10px 0; padding: 10px; border-left: 4px solid #ddd; }
        .passed { border-left-color: #4CAF50; }
        .failed { border-left-color: #f44336; }
        .recommendations { background: #fff3cd; padding: 15px; border-radius: 8px; margin: 20px 0; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Cross-Browser Animation Test Report</h1>
        <p><strong>Browser:</strong> ${report.browserInfo.name} ${report.browserInfo.version}</p>
        <p><strong>Engine:</strong> ${report.browserInfo.engine}</p>
        <p><strong>Timestamp:</strong> ${report.timestamp}</p>
    </div>

    <div class="summary">
        <div class="metric">
            <h3>Test Results</h3>
            <p>Passed: ${report.passedTests}/${report.totalTests}</p>
            <p>Failed: ${report.failedTests}</p>
        </div>
        <div class="metric">
            <h3>Performance</h3>
            <p>Average FPS: ${report.averagePerformance.fps.toFixed(1)}</p>
            <p>Memory Usage: ${(report.averagePerformance.memoryUsage * 100).toFixed(1)}%</p>
            <p>Dropped Frames: ${report.averagePerformance.droppedFrames}</p>
        </div>
    </div>

    <h2>Test Results</h2>
    ${report.testResults.map(result => `
        <div class="test-result ${result.passed ? 'passed' : 'failed'}">
            <h3>${result.testName} ${result.passed ? '✅' : '❌'}</h3>
            <p>FPS: ${result.performance.averageFPS.toFixed(1)} | Duration: ${result.duration.toFixed(2)}ms</p>
            ${result.errors.length > 0 ? `<p><strong>Errors:</strong> ${result.errors.join(', ')}</p>` : ''}
            ${result.warnings.length > 0 ? `<p><strong>Warnings:</strong> ${result.warnings.join(', ')}</p>` : ''}
        </div>
    `).join('')}

    ${report.recommendations.length > 0 ? `
        <div class="recommendations">
            <h2>Recommendations</h2>
            <ul>
                ${report.recommendations.map(rec => `<li>${rec}</li>`).join('')}
            </ul>
        </div>
    ` : ''}
</body>
</html>`;
  }

  private generateCSVReport(report: TestReport): string {
    const headers = ['Test Name', 'Passed', 'FPS', 'Duration (ms)', 'Dropped Frames', 'Errors', 'Warnings'];
    const rows = report.testResults.map(result => [
      result.testName,
      result.passed ? 'Yes' : 'No',
      result.performance.averageFPS.toFixed(1),
      result.duration.toFixed(2),
      result.performance.droppedFrames.toString(),
      result.errors.join('; '),
      result.warnings.join('; ')
    ]);

    return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  }
}

// Export singleton instance
export const automatedTester = new AutomatedAnimationTester();

// Utility functions
export const runBasicAnimationTests = () => automatedTester.runTestSuite('basic-animations');
export const runPerformanceStressTests = () => automatedTester.runTestSuite('performance-stress');
export const runBrowserSpecificTests = () => automatedTester.runTestSuite('browser-specific');
export const runAllTests = () => automatedTester.runAllTestSuites();